// src/utils/decryptAsync.js
//
// Promise-based wrapper around a single persistent Web Worker that runs the
// heavy CryptoJS AES decrypt off the main thread. Only use this for LARGE
// payloads (chapter/footnote HTML) — for short strings (book titles,
// chapter names, etc.) the synchronous decrypt() in security.js is fast
// enough that the worker round-trip overhead isn't worth it; keep using
// decrypt() there.
//
// BUILD-TOOLING NOTE: an earlier version of this file constructed the
// worker via `new Worker(new URL('../workers/decryptWorker.js', import.meta.url))`,
// which only works on webpack 5 (react-scripts 5+). If your project is on
// an older toolchain, `import.meta.url` can fail to even parse — not just
// fail at runtime — which breaks the whole importing module, not just
// decrypt. To avoid that entirely, this version builds the worker from an
// inline string via a Blob URL, and loads CryptoJS inside the worker with
// importScripts() from a CDN instead of an ES import. That makes worker
// construction independent of any bundler-specific syntax — it works the
// same on react-scripts 3, 4, or 5, or any other webpack config.
//
// Trade-off: the worker now loads CryptoJS from a CDN (cdnjs) at runtime
// instead of bundling your project's installed crypto-js version. If your
// CSP blocks external scripts, or you need to pin an exact crypto-js
// version/build, tell me and I'll switch this to inline the library source
// directly instead (larger file, but no external request).

import { decrypt as syncDecrypt } from './security';

const CRYPTOJS_CDN_URL = 'https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.2.0/crypto-js.min.js';

// The worker's entire source, as a string. Deliberately has no imports of
// its own — importScripts() is a standard, universally-supported classic
// Worker API that doesn't depend on the bundler understanding ES modules
// inside worker scripts at all.
const WORKER_SOURCE = `
  importScripts(${JSON.stringify(CRYPTOJS_CDN_URL)});

  var SECRET = ${JSON.stringify(process.env.REACT_APP_ENCRYPTION_KEY || 'XXXXXXXXXX')};

  self.onmessage = function (event) {
    var data = event.data || {};
    var id = data.id;
    var ciphertext = data.ciphertext;

    if (!ciphertext || typeof ciphertext !== 'string' || ciphertext.indexOf('U2FsdGVkX1') !== 0) {
      self.postMessage({ id: id, result: ciphertext });
      return;
    }

    try {
      var bytes = CryptoJS.AES.decrypt(ciphertext, SECRET);
      var originalText = bytes.toString(CryptoJS.enc.Utf8);
      self.postMessage({ id: id, result: originalText || ciphertext });
    } catch (err) {
      self.postMessage({ id: id, result: ciphertext, error: true });
    }
  };
`;

let worker = null;
let workerFailed = false;
let requestId = 0;
const pending = new Map();

function getWorker() {
  if (workerFailed) return null;
  if (worker) return worker;

  try {
    const blob = new Blob([WORKER_SOURCE], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);
    worker = new Worker(blobUrl);
    // The worker holds its own copy of the source once started; the object
    // URL itself can be released immediately rather than held for the
    // worker's whole lifetime.
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.warn('[decryptAsync] Failed to construct decrypt worker, falling back to synchronous decrypt:', err);
    workerFailed = true;
    return null;
  }

  worker.onmessage = (event) => {
    const { id, result } = event.data || {};
    const entry = pending.get(id);
    if (!entry) return;
    pending.delete(id);
    // security.js's decrypt() fails open (returns ciphertext) rather than
    // throwing on a bad chunk, so we mirror that here rather than
    // rejecting — callers already expect "decrypt may just hand back the
    // input" as a possible outcome, not necessarily an error.
    entry.resolve(result);
  };

  worker.onerror = (err) => {
    // The worker crashed entirely (CDN blocked by CSP/network, bad build,
    // etc.) — reject everything currently in flight so callers don't hang
    // forever, and permanently fall back to the synchronous path for the
    // rest of this session rather than retrying a broken worker.
    console.warn('[decryptAsync] Decrypt worker crashed, falling back to synchronous decrypt for the rest of this session:', err);
    pending.forEach(({ reject }) => reject(err));
    pending.clear();
    workerFailed = true;
    worker = null;
  };

  return worker;
}

export function decryptAsync(ciphertext) {
  if (!ciphertext || typeof ciphertext !== 'string') return Promise.resolve(ciphertext);
  // Cheap short-circuit for already-plain content — matches security.js's own
  // check, avoids a worker round-trip for content that isn't encrypted at all.
  if (!ciphertext.startsWith('U2FsdGVkX1')) return Promise.resolve(ciphertext);

  if (typeof Worker === 'undefined') {
    // No Worker support in this environment at all.
    return Promise.resolve(syncDecrypt(ciphertext));
  }

  const w = getWorker();
  if (!w) {
    // Worker construction failed — fail safe to the old synchronous path
    // rather than hanging the caller.
    return Promise.resolve(syncDecrypt(ciphertext));
  }

  const id = ++requestId;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    try {
      w.postMessage({ id, ciphertext });
    } catch (err) {
      pending.delete(id);
      resolve(syncDecrypt(ciphertext));
    }
  }).catch(() => syncDecrypt(ciphertext));
}
